/**
 * 라이트/다크 테마 전환 store. localStorage 에 사용자 선택 저장,
 * 시스템 prefers-color-scheme 을 default 로.
 *
 * 부수효과: theme 변경 시 document.documentElement 의 `class` 에
 * "dark" 추가/제거 — Tailwind 의 `dark:` 변형 활성화.
 */
import { create } from "zustand";

export type Theme = "light" | "dark";

const STORAGE_KEY = "devit.web.theme";

function readInitialTheme(): Theme {
  if (typeof window === "undefined") return "light";
  const saved = window.localStorage.getItem(STORAGE_KEY) as Theme | null;
  if (saved === "light" || saved === "dark") return saved;
  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? "dark"
    : "light";
}

function applyTheme(theme: Theme): void {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (theme === "dark") root.classList.add("dark");
  else root.classList.remove("dark");
}

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  toggle: () => void;
}

export const useThemeStore = create<ThemeState>((set, get) => {
  const initial = readInitialTheme();
  applyTheme(initial);

  return {
    theme: initial,
    setTheme(theme): void {
      applyTheme(theme);
      window.localStorage.setItem(STORAGE_KEY, theme);
      set({ theme });
    },
    toggle(): void {
      get().setTheme(get().theme === "dark" ? "light" : "dark");
    },
  };
});
